import { CustomFieldsService } from './../../../services/custom-fields.service';
import { Component, Input, forwardRef, OnInit, OnDestroy } from '@angular/core';
import { NgForm, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { AuthenticationService } from '../../../services/authentication.service';
import { StatsReport, ReportDimension, ReportMetric } from '../../../interfaces/report';
import { CustomField } from '../../../interfaces/publication';


@Component({
	selector: 'report-columns',
	templateUrl: './report-columns.component.html',
	styleUrls: ['./report-columns.component.scss'],
	providers: [{
		provide: NG_VALUE_ACCESSOR,
		useExisting: forwardRef(() => ReportColumnsComponent),
		multi: true
	}]
})

export class ReportColumnsComponent implements ControlValueAccessor, OnInit, OnDestroy {
	@Input() form: NgForm;

	subscriptions: Subscription;
	dateRangePresets: any[];
	testValues = ['today', 'last-7-days'];
	columns: any = [];
	report: StatsReport;
	disabledOptions: string[];
	disableInitsAndPercentages = false;
	dimensions: any;
	openCols: any = {};

	constructor(
		private translate: TranslateService,
		private authenticationService: AuthenticationService,
		private customFieldsService: CustomFieldsService,
	) {
		this.subscriptions = new Subscription();

		this.columns = [];
	}

	ngOnInit() {
		const dimensionOptions = [
			{ value: 'granularity', label: this.translate.instant('TIME_DAY'), unit: 'day' },
			{ value: 'playout', label: this.translate.instant('PLAYOUT') },
			{ value: 'domain', label: this.translate.instant('WEB_DOMAIN') },
			{ value: 'custom_stats', label: this.translate.instant('CUSTOM_STATS_EVENT') },
			{ value: 'quartiles', label: this.translate.instant('VAST_QUARTILES') },
			{ value: 'device_type', label: this.translate.instant('DEVICE_TYPE') },
			{ value: 'country', label: this.translate.instant('COUNTRY') },
			{ value: 'region', label: this.translate.instant('REGION') },
			{ value: 'os', label: this.translate.instant('OPERATING_SYSTEM') },
			{ value: 'browser', label: this.translate.instant('BROWSER') }
		];

		this.dimensions = {
			title: this.translate.instant('DIMENSIONS'),
			property: 'dimensions',
			options: dimensionOptions,
			values: [],
			limit: 20
		};
		this.setOptions();
		this.subscriptions.add(this.customFieldsService.mediaclipCustomFields.subscribe((customFields: CustomField[]) => {
			this.dimensions.options = [...dimensionOptions, ...customFields.filter((customField) => customField.includeInStats).map((customField) => ({
				label: customField.label,
				value: customField.name
			}))];
		}));
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	setOptions() {
		this.columns.push({
			title: this.translate.instant('CLIP_INFORMATION'),
			property: 'mediaclip',
			options: [
				{ value: 'id', label: this.translate.instant('ID') },
				{ value: 'title', label: this.translate.instant('TITLE') },
				{ value: 'length', label: this.translate.instant('DURATION') },
				{ value: 'published_date', label: this.translate.instant('PUBLISHED_DATE') },
				{ value: 'status', label: this.translate.instant('STATUS') },
				{ value: 'mediatype', label: this.translate.instant('MEDIATYPE') },
				{ value: 'sourcetype', label: this.translate.instant('SOURCETYPE') },
				{ value: 'description', label: this.translate.instant('DESCRIPTION') },
				{ value: 'tags', label: this.translate.instant('TAGS') },
				{ value: 'deeplink', label: this.translate.instant('DEEP_LINK') },
				{ value: 'author', label: this.translate.instant('AUTHOR') },
				{ value: 'copyright', label: this.translate.instant('COPYRIGHT') },
				{ value: 'created_date', label: this.translate.instant('CREATED_DATE') },
				{ value: 'updated_date', label: this.translate.instant('UPDATED_DATE') },
				{ value: 'originalfilename', label: this.translate.instant('ORIGINAL_FILENAME') },
				{ value: 'disablecommercials', label: this.translate.instant('DISABLE_COMMERCIALS') },
				{ value: 'embargoFrom', label: this.translate.instant('EMBARGO_AVAILABLE_FROM') },
				{ value: 'embargoTo', label: this.translate.instant('EMBARGO_AVAILABLE_TO') },
				{ value: 'createdBy', label: this.translate.instant('CREATED_BY') }
			],
			values: [],
			limit: 20
		});

		this.subscriptions.add(this.authenticationService.hasPermission('reportsAllTimeViews').subscribe((hasPermission) => {
			if (hasPermission) {
				this.columns[0].options.push({ value: 'views', label: this.translate.instant('ALL_TIME_VIEWS') });
			}
		}));
		this.subscriptions.add(this.authenticationService.getAllowedCustomFields().subscribe((fields: CustomField[]) => {
			fields.forEach((field: CustomField) => {
				this.columns[0].options.push({ label: field.label, value: field.name });
			});
		}));

		this.columns.push(this.dimensions);

		this.columns.push({
			title: this.translate.instant('METRICS'),
			property: 'metrics',
			options: [
				{ value: 'views', label: this.translate.instant('VIEWS') },
				{ value: 'creativeStart', label: this.translate.instant('AD_IMPRESSIONS') },
				{ value: 'mainContentStarted', label: this.translate.instant('MAIN_CONTENT_STARTS') },
				{ value: 'completed', label: this.translate.instant('COMPLETED_VIEWS') },
				{ value: 'inits', label: this.translate.instant('PLAYER_LOADS') },
				{ value: 'viewTime', label: this.translate.instant('VIEW_TIME') },
				{ value: 'viewTimeAverage', label: this.translate.instant('VIEW_TIME_AVERAGE') },
				{ value: 'viewcount20percent', label: this.translate.instant('VIEW_COUNT_PERCENTAGE', { percentage: 20 }) },
				{ value: 'viewcount40percent', label: this.translate.instant('VIEW_COUNT_PERCENTAGE', { percentage: 40 }) },
				{ value: 'viewcount60percent', label: this.translate.instant('VIEW_COUNT_PERCENTAGE', { percentage: 60 }) },
				{ value: 'viewcount80percent', label: this.translate.instant('VIEW_COUNT_PERCENTAGE', { percentage: 80 }) }
			],
			values: [],
			limit: 20
		});
	}

	columnChanged(column: any, values: string[]) {
		let mediaclipDimensions: any = null;
		if (this.report.dimensions) {
			mediaclipDimensions = this.report.dimensions.find(dimension => dimension.value === 'mediaclip');
		}
		const newDimensions: ReportDimension[] = [];
		const newMetrics: ReportMetric[] = [];
		this.columns.forEach((c: any) => {
			switch (c.property) {
				case 'mediaclip':
					if (c.values && c.values.length && !mediaclipDimensions) {
						mediaclipDimensions = {
							value: 'mediaclip',
							label: this.translate.instant('MEDIACLIP'),
							properties: []
						};
					}
					mediaclipDimensions.properties = [...c.values];
					newDimensions.push(mediaclipDimensions);
					break;
				case 'dimensions':
					c.options.forEach((option: any) => {
						if (c.values.indexOf(option.value) !== -1) {
							newDimensions.push(option);
						}
					});
					break;
				case 'metrics':
					c.options.forEach((option: any) => {
						if (c.values.indexOf(option.value) !== -1) {
							newMetrics.push(option);
						}
					});
					break;
			}
		});
		this.report.dimensions = newDimensions;
		this.report.metrics = newMetrics;
		this.checkDisabledMetrics();
		this.propagateChange(this.report);
	}

	checkDisabledMetrics() {
		if (this.report && this.report.dimensions && this.report.dimensions.length > 1) {
			this.disabledOptions = ['inits', 'viewcount20percent', 'viewcount40percent', 'viewcount60percent', 'viewcount80percent'];
			this.disableInitsAndPercentages = true;
		} else {
			this.disabledOptions = [];
			this.disableInitsAndPercentages = false;
		}
	}

	writeValue(value: StatsReport) {
		this.report = value;
		const mcCol = this.columns.find((col: any) => col.property === 'mediaclip');
		const dimensionsCol = this.columns.find((col: any) => col.property === 'dimensions');
		const metricsCol = this.columns.find((col: any) => col.property === 'metrics');

		if (this.report) {
			this.checkDisabledMetrics();
			if (this.report.metrics) {
				metricsCol.values = this.report.metrics.map(metric => metric.value);
			}
			if (this.report.dimensions) {
				this.report.dimensions.forEach((dimension) => {
					switch (dimension.value) {
						case 'mediaclip':
							mcCol.values = dimension.properties || [];
							if (!mcCol.values.includes('id')) {
								// id should always be included
								mcCol.values.unshift('id');
							}
							break;
					}
				});

				dimensionsCol.values = this.report.dimensions.map(dimension => dimension.value);
			}
		}
	}

	propagateChange = (_: any) => { };

	registerOnChange(fn: any) {
		this.propagateChange = fn;
	}

	registerOnTouched() {

	}
}
